import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  ArrowLeft,
  BadgeCheck,
  Wallet,
  ShieldCheck,
  CalendarClock,
  TrendingUp,
  Sparkles,
  CheckCircle2,
  Store,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import FooterSection from '../components/FooterSection';
import SEO from '../components/SEO';
import { LanguageProvider } from '../context/LanguageContext';

// ---------------------------------------------------------------------------
// Member Benefits — DEDICATED page about RESTAURANTS.
// Per spec: this page must NEVER mention partner-side benefits
// (commissions, partner dashboard, referral tracking, Stripe Connect).
// ---------------------------------------------------------------------------

function BenefitTile({ icon: Icon, title, body, accent }) {
  return (
    <div
      className={`relative rounded-2xl border p-7 transition-all hover:border-emerald-500/40 ${
        accent
          ? 'bg-gradient-to-br from-emerald-500/[0.08] to-emerald-500/[0.02] border-emerald-500/30'
          : 'bg-white/[0.025] border-white/[0.06]'
      }`}
    >
      <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/[0.04] blur-3xl rounded-full pointer-events-none" />
      <div
        className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${
          accent
            ? 'bg-emerald-500 text-black'
            : 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-300'
        }`}
      >
        <Icon className="w-5 h-5" />
      </div>
      <h3 className="text-lg font-bold text-white mb-2 leading-tight">{title}</h3>
      <p className="text-sm text-white/65 leading-relaxed">{body}</p>
    </div>
  );
}

export default function PartnersMemberBenefits() {
  return (
    <LanguageProvider>
      <Inner />
    </LanguageProvider>
  );
}

function Inner() {
  const { t } = useTranslation();
  const BENEFITS = [
    { icon: BadgeCheck, title: t('partners.mbenefits.b1_title'), body: t('partners.mbenefits.b1_body'), accent: true },
    { icon: Wallet, title: t('partners.mbenefits.b2_title'), body: t('partners.mbenefits.b2_body') },
    { icon: ShieldCheck, title: t('partners.mbenefits.b3_title'), body: t('partners.mbenefits.b3_body') },
    { icon: CalendarClock, title: t('partners.mbenefits.b4_title'), body: t('partners.mbenefits.b4_body') },
    { icon: TrendingUp, title: t('partners.mbenefits.b5_title'), body: t('partners.mbenefits.b5_body') },
  ];

  const FAST_NOFEE_BENEFITS = [
    t('partners.mbenefits.fast_li_1'),
    t('partners.mbenefits.fast_li_2'),
    t('partners.mbenefits.fast_li_3'),
    t('partners.mbenefits.fast_li_4'),
    t('partners.mbenefits.fast_li_5'),
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'NoFee — Restaurant Benefits',
    provider: { '@type': 'Organization', name: 'NoFee', url: 'https://nofee.restaurant' },
    description:
      '0% delivery app fees, direct Stripe payouts, 45-day free trial and the FAST NOFEE badge program for member restaurants joining through Partner Organizations.',
  };

  return (
    <div className="min-h-screen bg-black text-white antialiased">
      <SEO
        title="Member Benefits — NoFee Partners"
        description="What your member restaurants receive when joining NoFee: 0% delivery app fees, direct Stripe payouts, 45-day free trial and the FAST NOFEE badge."
        path="/partners/member-benefits"
        jsonLd={jsonLd}
      />
      <Navbar forceSolid />

      <main className="pt-32 lg:pt-40 pb-24">
        <div className="max-w-[1180px] mx-auto px-6 lg:px-10">
          <Link
            to="/partners"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/55 hover:text-emerald-300 mb-6 transition-colors"
            data-testid="member-benefits-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> {t('partners.mbenefits.back')}
          </Link>

          {/* Page header */}
          <div className="max-w-[760px] mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/[0.08] border border-emerald-500/25 text-[11.5px] uppercase tracking-[0.22em] text-emerald-300 font-bold mb-5">
              <Store className="w-3.5 h-3.5" /> {t('partners.mbenefits.kicker')}
            </div>
            <h1
              className="text-4xl lg:text-6xl font-black tracking-[-0.02em] text-white leading-[1.04]"
              data-testid="member-benefits-title"
            >
              {t('partners.mbenefits.title_a')} <span className="bg-gradient-to-b from-emerald-200 to-emerald-400 bg-clip-text text-transparent">{t('partners.mbenefits.title_b')}</span>.
            </h1>
            <p className="mt-6 text-lg text-white/65 max-w-[640px] leading-relaxed">
              {t('partners.mbenefits.subtitle')}
            </p>
          </div>

          {/* Benefits grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10" data-testid="member-benefits-grid">
            {BENEFITS.map((b) => (
              <BenefitTile key={b.title} {...b} />
            ))}
          </div>

          {/* FAST NOFEE — featured separately with sub-benefits */}
          <div className="rounded-3xl border border-emerald-500/25 bg-gradient-to-br from-emerald-500/[0.05] to-black/30 p-8 lg:p-10 mb-16" data-testid="member-benefits-fast-nofee">
            <div className="grid lg:grid-cols-[auto_1fr] gap-7 items-start">
              <div className="w-14 h-14 rounded-2xl bg-emerald-500 text-black flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6" />
              </div>
              <div>
                <p className="text-[10.5px] uppercase tracking-[0.24em] text-emerald-300 font-bold mb-2">
                  {t('partners.mbenefits.fast_kicker')}
                </p>
                <h2 className="text-2xl lg:text-3xl font-black tracking-tight text-white mb-2">
                  {t('partners.mbenefits.fast_title')}
                </h2>
                <p className="text-white/65 leading-relaxed mb-5 max-w-[640px]">
                  {t('partners.mbenefits.fast_body')}
                </p>
                <ul className="grid sm:grid-cols-2 gap-2.5">
                  {FAST_NOFEE_BENEFITS.map((b) => (
                    <li key={b} className="flex items-center gap-2 text-sm text-white/80">
                      <CheckCircle2 className="w-4 h-4 text-emerald-300 flex-shrink-0" />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* CTA strip */}
          <div className="rounded-3xl border border-emerald-500/25 bg-gradient-to-br from-zinc-900 to-black p-8 lg:p-12">
            <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8 items-center">
              <div>
                <p className="text-[11px] uppercase tracking-[0.28em] text-emerald-300 font-bold mb-3">
                  {t('partners.mbenefits.cta_label')}
                </p>
                <h2 className="text-2xl lg:text-4xl font-black tracking-tight text-white leading-tight">
                  {t('partners.mbenefits.cta_title')}
                </h2>
                <p className="mt-4 text-white/60 max-w-[560px] leading-relaxed">
                  {t('partners.mbenefits.cta_subtitle')}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/partners/onboarding"
                  className="inline-flex items-center gap-2 h-13 px-6 py-3.5 rounded-2xl bg-gradient-to-b from-emerald-300 to-emerald-500 hover:from-emerald-200 hover:to-emerald-400 text-black font-extrabold text-[15px] transition-all"
                  data-testid="member-benefits-cta-apply"
                >
                  {t('partners.mbenefits.cta_apply')} <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/partners/partner-benefits"
                  className="inline-flex items-center gap-2 h-13 px-6 py-3.5 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/15 hover:border-emerald-500/40 text-white font-bold text-[14px] transition-all"
                  data-testid="member-benefits-cta-partner"
                >
                  {t('partners.mbenefits.cta_partner')} <CheckCircle2 className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>

      <FooterSection />
    </div>
  );
}
